import React, {
  createContext,
  useEffect,
  useReducer,
  useState,
  useContext,
} from "react";
//REDUCER
import {
  BundleReducer,
  CommunityReducer,
  ProfileReducer,
  QuestionReducer,
} from "../reducers";

//CONSTANT
import { ACTION_EVENT } from "../constant/event";
import { SUBSCRIPTION_STATUS } from '../../app_constant/cissp';
import {
  BUNDLE_APP_NAME,
  GET_APP_DOMAIN_LIST,
  GET_APP_NAME,
  IS_BUNDLE_APP
} from "../../app_constant";
import { createLogger } from "../utils/logger";


//API
import { UPDATE_SUBSCRIPTION } from "../api/community";
import {
  GET_STRIPE_DETAILS,
  GET_USER_STRIPE_ONE_TIME_PAYMENT,
  UPDATE_USER_SUBSCRIPTION,
  GET_REVENUECAT_EVENTS,
  GET_REVENUECAT_METADATA,
  GET_REVENUECAT_SUBSCRIPTION_PLAN,
} from "../api/firebase_user";
import { GET_SCORE_QUESTIONS, SAVE_QUESTION } from "../api/questions";
import {
  GET_ACCESS_CODE_DETAILS,
  UPDATE_ACCESS_CODE_REDEEM,
} from "../api/access_code";
import { FETCH_BUNDLE_DETAILS } from '../api/activation';

//CONTENT
import { GET_LOCAL_QUESTIONS } from "../api/content";


//CONTEXT
import { BundleContext } from "./BundleProvider";

//PACKAGES
import { auth, firestore } from "../api/config";

import moment from "moment";



export const AuthContext = createContext();

export const AuthProvider = (props) => {
  const { selectedCertificate } = useContext(BundleContext);

  const [mySubscription, setMySubscription] = useState(null);
  
  let logger = createLogger(process.env.REACT_APP_ENV === 'DEVELOPMENT', 'AuthProvider');

  const [bundleData, dispatchBundle] = useReducer(
    BundleReducer.reducer,
    BundleReducer.initial
  );
  const [profile, setProfileState] = useState(ProfileReducer.initial);
  const [profilePromise, setProfilePromise] = useState(null);

  // Custom dispatch function to handle async reducer operations
  const dispatch = async (action) => {
    try {
      // Call the async reducer
      const result = await ProfileReducer.reducer(profile, action);
      
      // Update the state with the result
      setProfileState(result);
      
      // Clear any pending promise
      setProfilePromise(null);
      
      return result;
    } catch (error) {
      logger.error('[AuthProvider] Error in profile dispatch:', error);
      setProfilePromise(null);
      throw error;
    }
  };
  const [community, dispatchCommunity] = useReducer(
    CommunityReducer.reducer,
    CommunityReducer.initial
  );
  const [questionScrore, dispatchQuestionScore] = useReducer(
    QuestionReducer.reducer,
    QuestionReducer.initial
  );

  async function getScoreQuestions() {
    logger.log('[AuthProvider.getScoreQuestions] Starting to fetch score questions');
    const result = await GET_SCORE_QUESTIONS();
    logger.log('[AuthProvider.getScoreQuestions] Received result:', result);
    setAllQuestionData(result?.data ?? []);
    dispatchQuestionScore({
      type: ACTION_EVENT.SET_ALL_QUESTION_SCORE,
      payload: result?.data ?? [],
    });
    return { data: result?.data ?? [], exist: result.isExist };
  }

  // Only load score questions when user is authenticated AND bundle is selected
  useEffect(() => {
    const currentUser = auth().currentUser;
    if (currentUser && selectedCertificate) {
      logger.log('[AuthProvider] User authenticated and bundle selected, loading score questions');
      getScoreQuestions();
    } else {
      if (!currentUser) {
        logger.log('[AuthProvider] No user authenticated, skipping score questions');
      } else if (!selectedCertificate) {
        logger.log('[AuthProvider] No bundle selected, skipping score questions');
      }
    }
  }, [selectedCertificate]);

  const [questionsList, setQuestionsList] = useState([]);
  const [isSubscribe, setSubscription] = useState(false);
  const [showReactivation, setShowReactivation] = useState(false);
  const [isVisibleCommunity, setVisibleCommunity] = useState(false);
  const [loggingEnabled, setLoggingEnabled] = useState(false);


  

  useEffect(() => {
    const QUESTIONS_LIST = GET_LOCAL_QUESTIONS(selectedCertificate);
    setQuestionsList(QUESTIONS_LIST);
  }, [selectedCertificate]);

  useEffect(() => {
    logger.log('[AuthProvider.useEffect] Setting up auth state listener');
    const unsubscribeAuth = auth().onAuthStateChanged((user) => {
      logger.log('[AuthProvider.useEffect] Auth state changed:', user ? 'User logged in' : 'No user');
      if (user) {
        const APP_NAME = GET_APP_NAME(selectedCertificate);
        logger.log('[AuthProvider.useEffect] App name:', APP_NAME);

        const unsubscribeProfile = firestore()
          .collection("apps")
          .doc(APP_NAME)
          .collection("users")
          .doc(user.uid)
          .onSnapshot((snapshot) => {
            logger.log('[AuthProvider.useEffect] Profile update received:', snapshot.exists);
            if (snapshot.exists) {
              setProfile(snapshot.data());
            }
          });

        const unsubscribeQuestions = firestore()
          .collection("apps")
          .doc(APP_NAME)
          .collection("users")
          .doc(user.uid)
          .collection("questions")
          .onSnapshot((querySnapshot) => {
            logger.log('[AuthProvider.useEffect] Questions update received:', querySnapshot.size, 'questions');
            const questions = [];
            querySnapshot.forEach((doc) => {
              questions.push(doc.data());
            });
            setQuestions(questions);
          });

        const unsubscribeAppConfig = firestore()
          .collection("apps")
          .doc(APP_NAME)
          .collection("APP")
          .doc("CONFIG")
          .onSnapshot((snapshot) => {
            logger.log('[AuthProvider.useEffect] App config update received:', snapshot.exists);
            if (snapshot.exists) {
              setVisibleCommunity(snapshot.data()?.visible_community ?? false);
              setLoggingEnabled(snapshot.data()?.logging_enabled ?? false);
            // Change logger when we get this data
            logger = createLogger(snapshot.data()?.logging_enabled ?? false);
            }
          });

        let unsubscribeBundle = null;
        if (IS_BUNDLE_APP) {
          logger.log('[AuthProvider.useEffect] Setting up bundle subscription');
          (async () => {
            try {
              const bundleAppName = await BUNDLE_APP_NAME;
              
              // Validate bundleAppName
              if (!bundleAppName || typeof bundleAppName !== 'string') {
                logger.error("Invalid bundleAppName:", bundleAppName);
                return;
              }

              unsubscribeBundle = firestore()
                .collection("apps")
                .doc(bundleAppName)
                .collection("users")
                .doc(user.uid)
                .onSnapshot((snapshot) => {
                  logger.log('[AuthProvider.useEffect] Bundle update received:', snapshot.exists);
                  if (snapshot.exists) {
                    setBundleProfile(snapshot.data());
                  }
                });
            } catch (error) {
              logger.error("Error setting up bundle subscription:", error);
            }
          })();
        }

        return () => {
          logger.log('[AuthProvider.useEffect] Cleaning up subscriptions');
          unsubscribeProfile();
          unsubscribeQuestions();
          unsubscribeBundle && unsubscribeBundle();
          unsubscribeAppConfig();
        };
      }
    });

    return () => {
      logger.log('[AuthProvider.useEffect] Cleaning up auth listener');
      unsubscribeAuth();
    };
  }, [selectedCertificate]);


  const normalizeExpirationDate = (expirationDate) => {
    logger.log('[AuthProvider.normalizeExpirationDate] Input date:', expirationDate);
    let expirationMs = new Date(expirationDate).getTime();
    const normalized = expirationMs < 1e12 ? expirationMs * 1000 : expirationMs;
    logger.log('[AuthProvider.normalizeExpirationDate] Normalized date:', normalized);
    return normalized;
  };

  const isSubscriptionActive = (expirationDate) => {
    logger.log('[AuthProvider.isSubscriptionActive] Checking expiration:', expirationDate);
    const isActive = normalizeExpirationDate(expirationDate) > Date.now();
    logger.log('[AuthProvider.isSubscriptionActive] Is active:', isActive);
    return isActive;
  };

  async function FETCH_EXAM_DETAILS(uid, appCollectionName) {
    const authRef = firestore()
        .collection("apps")
        .doc(appCollectionName)
        .collection("users");

    try {
        const doc = await authRef.doc(uid).get();

        if (doc.exists) {
            return doc.data();
        } else {
            return null;
        }

    } catch (error) {
        logger.error("Error fetching exam details:", error);
    }
}

  async function getSubscriptionDetails() {
    logger.log('[AuthProvider.getSubscriptionDetails] Starting subscription check for user: ' + auth().currentUser?.uid);
    const bundleAppName = await BUNDLE_APP_NAME;
    logger.log('[AuthProvider.getSubscriptionDetails] Bundle collection name:', bundleAppName);
    
    try {
      let isSubscribed = false;
      
      // Check if user is authenticated
      const currentUser = auth().currentUser;
      logger.log('[AuthProvider.getSubscriptionDetails] Current user:', currentUser ? currentUser.uid : 'null');
      
      if (!currentUser) {
        logger.log('[AuthProvider.getSubscriptionDetails] No authenticated user found');
        setShowReactivation(false);
        setSubscription(false);
        return false;
      }
      
      const uid = currentUser.uid;
      logger.log('[AuthProvider.getSubscriptionDetails] Fetching bundle details for user:', uid);
      const user = await FETCH_BUNDLE_DETAILS(uid);
      logger.log('[AuthProvider.getSubscriptionDetails] Bundle details:', user);
      
      if (user) {
        // user is already the data object, no need to call .data()
        const data = user;
        
        // Check if subscription exists for the current certificate and is not expired
        if (data && data.subscriptions && data.subscriptions[selectedCertificate]) {
          const subscription = data.subscriptions[selectedCertificate];
          const expirationTimestamp = subscription.subscription_expiration;
          // Convert Firestore Timestamp to milliseconds
          const expirationDate = expirationTimestamp.toMillis();
          const currentTime = Date.now();
          
          logger.log('[AuthProvider.getSubscriptionDetails] Subscription expiration (ms):', expirationDate);
          logger.log('[AuthProvider.getSubscriptionDetails] Current time (ms):', currentTime);
          
          if (expirationDate > currentTime) {
            logger.log('[AuthProvider.getSubscriptionDetails] Subscription is active');
            isSubscribed = true;
            setMySubscription(subscription);
          } else {
            logger.log('[AuthProvider.getSubscriptionDetails] Subscription has expired');
          }
        } else {
          logger.log('[AuthProvider.getSubscriptionDetails] No subscription found for certificate:', selectedCertificate);
        }
      } else {
        logger.log('[AuthProvider.getSubscriptionDetails] No user data found');
      }
      
      logger.log('[AuthProvider.getSubscriptionDetails] Final subscription status:', isSubscribed);
      setShowReactivation(!isSubscribed);
      setSubscription(isSubscribed);
      return isSubscribed;
    } catch (err) {
      logger.error('[AuthProvider.getSubscriptionDetails] Subscription check failed:', err);
      setShowReactivation(false);
      setSubscription(false);
      return false;
    }
  }

  async function setProfile(data) {
    logger.log('[AuthProvider.setProfile] Setting profile data:', data);
    try {
      await dispatch({ type: ACTION_EVENT.SET_PROFILE, payload: data });
    } catch (error) {
      logger.error('[AuthProvider.setProfile] Error setting profile:', error);
    }
  }

  function setQuestions(questions) {
    logger.log('[AuthProvider.setQuestions] Setting questions:', questions?.length);
    dispatchQuestionScore({ type: ACTION_EVENT.SET_ALL_QUESTION_SCORE, payload: questions ?? [], });
  }

  function setBundleProfile(data) {
    logger.log('[AuthProvider.setBundleProfile] Setting bundle profile:', data);
    dispatchBundle({ type: ACTION_EVENT.SET_PROFILE, payload: data });
  }

  async function setNewProfile(data) {
    try {
      await dispatch({ type: ACTION_EVENT.SET_NEW_PROFILE, payload: data });
    } catch (error) {
      logger.error('[AuthProvider.setNewProfile] Error setting new profile:', error);
    }
  }

  function setNewBundleProfile(data) {
    dispatchBundle({ type: ACTION_EVENT.SET_NEW_PROFILE, payload: data });
  }

  function setNotification(quiz, study) {
    dispatch({ type: ACTION_EVENT.SET_NOTIFICATION, payload: { quiz, study } });
  }

  function setProfileDetails(firstName, lastName) {
    if (IS_BUNDLE_APP) {
      dispatchBundle({
        type: ACTION_EVENT.SET_PROFILE_DETAILS,
        payload: { firstName, lastName },
      });
    } else {
      dispatch({
        type: ACTION_EVENT.SET_PROFILE_DETAILS,
        payload: { firstName, lastName },
      });
    }
  }

  function setProfilePicture(url) {
    dispatch({
      type: ACTION_EVENT.SET_PROFILE_PICTURE,
      payload: { profile_pic: url },
    });
  }

  function setBundleProfilePicture(url) {
    dispatchBundle({
      type: ACTION_EVENT.SET_PROFILE_PICTURE,
      payload: { profile_pic: url },
    });
  }

  function setTestOptions(time_per_question, allow_go_back) {
    dispatch({
      type: ACTION_EVENT.SET_PROFILE_TEST_OPTIONS,
      payload: {
        time_per_question: time_per_question,
        allow_go_back: allow_go_back,
      },
    });
  }

  function setFontsOptions(font_size) {
    dispatch({
      type: ACTION_EVENT.SET_PROFILE_FONT_OPTIONS,
      payload: {
        font_size: font_size,
      },
    });
  }

  async function setTestHistory(data) {
    try {
      await dispatch({ type: ACTION_EVENT.SET_TEST_HISTORY, payload: data });
    } catch (error) {
      logger.error('[AuthProvider.setTestHistory] Error setting test history:', error);
    }
  }

  function deleteTestHistory(data) {
    dispatch({ type: ACTION_EVENT.DELETE_TEST_HISOTRY, payload: data });
  }

  function addFlashcardToBookmark(id) {
    dispatch({ type: ACTION_EVENT.ADD_FLASHCARD_TO_BOOKMARK, payload: id });
  }

  function removeFlashcardFromBookmark(id) {
    dispatch({
      type: ACTION_EVENT.REMOVE_FLASHCARD_FROM_BOOKMARK,
      payload: id,
    });
  }

  function addFlashcardProgress(setId, cardId) {
    const data = {
      flashcard_set_id: setId,
      last_seen_flashcard_id: cardId,
    };
    dispatch({ type: ACTION_EVENT.ADD_FLASHCARD_PROGRESS, payload: data });
  }

  function addQuestionToBookmark(id) {
    dispatch({ type: ACTION_EVENT.ADD_QUESTION_TO_BOOKMARK, payload: id });
  }

  function removeQuestionFromBookmark(id) {
    dispatch({ type: ACTION_EVENT.REMOVE_QUESTION_FROM_BOOKMARK, payload: id });
  }

  function addQuestionProgress(setId, questionId) {
    const data = {
      study_questions_set_id: setId,
      last_seen_question_id: questionId,
    };
    dispatch({ type: ACTION_EVENT.ADD_QUESTION_PROGRESS, payload: data });
  }

  function addQuestionScore(question_id, topic_id, correct) {
    logger.log('[AuthProvider.addQuestionScore] Adding score:', { question_id, topic_id, correct });

    const domains = GET_APP_DOMAIN_LIST(selectedCertificate);
    const data = {
      question_id: question_id,
      topic_id: topic_id,
      correct: correct,
      questionsList: questionsList,
      app_domains: domains,
    };

    let scores = questionScrore?.all_questions_scrores ?? [];
    const savedList = scores.filter((item) => item.question_id == question_id);
    const saveItem = savedList.length > 0 ? savedList[0] : null;
    
    if (saveItem) {
      logger.log('[AuthProvider.addQuestionScore] Updating existing score');
      const score = correct ? saveItem.score + 1 : 0;
      const data = {
        question_id: question_id,
        topic_id: topic_id,
        times_answered: saveItem.times_answered + 1,
        last_seen: moment().format("YYYY MM DD HH:mm:ss"),
        score: score < 0 ? 0 : score,
        accuracy_score: score == 0 ? 0 : score == 1 ? 0.9 : 1,
      };
      SAVE_QUESTION(data);
      scores = scores.filter((item) => item.question_id != question_id);
      scores.push(data);
    } else {
      logger.log('[AuthProvider.addQuestionScore] Creating new score');
      const score = correct ? 1 : 0;
      const data = {
        question_id: question_id,
        topic_id: topic_id,
        times_answered: 1,
        last_seen: moment().format("YYYY MM DD HH:mm:ss"),
        score: score,
        accuracy_score: score == 1 ? 0.9 : 0,
      };
      SAVE_QUESTION(data);
      scores.push(data);
    }

    data.scores = scores;
    logger.log('[AuthProvider.addQuestionScore] Final scores:', scores);
    dispatchQuestionScore({
      type: ACTION_EVENT.SET_ALL_QUESTION_SCORE,
      payload: scores,
    });
    dispatch({ type: ACTION_EVENT.ON_UPDATE_QUESTIONS_SCROE, payload: data });
    dispatchCommunity({
      type: ACTION_EVENT.ON_UPDATE_COMMUNITY_DATA,
      payload: data,
    });
  }

  function addAllQuestionScore(history) {
    const domains = GET_APP_DOMAIN_LIST(selectedCertificate);
    const data = {
      history: history,
      questionsList: questionsList,
      app_domains: domains,
    };

    let scores = questionScrore?.all_questions_scrores ?? [];
    for (let index in history) {
      const obj = history[index];
      const savedList = scores.filter(
        (item) => item.question_id == obj.question_id
      );
      const saveItem = savedList.length > 0 ? savedList[0] : null;
      const APP_DOMAIN_LIST = domains;
      if (saveItem) {
        const score = obj.correct ? saveItem.score + 1 : 0;
        const data = {
          question_id: obj.question_id,
          topic_id: obj.topic_id,
          times_answered: saveItem.times_answered + 1,
          last_seen: moment().format("YYYY MM DD HH:mm:ss"),
          score: score < 0 ? 0 : score,
          accuracy_score: score == 0 ? 0 : score == 1 ? 0.9 : 1,
        };
        SAVE_QUESTION(data);
        scores = scores.filter((item) => item.question_id != obj.question_id);
        scores.push(data);
      } else {
        const score = obj.correct ? 1 : 0;
        const data = {
          question_id: obj.question_id,
          topic_id: obj.topic_id,
          times_answered: 1,
          last_seen: moment().format("YYYY MM DD HH:mm:ss"),
          score: score,
          accuracy_score: score == 1 ? 0.9 : 0,
        };
        SAVE_QUESTION(data);
        scores.push(data);
      }
    }

    data.scores = scores;
    dispatchQuestionScore({
      type: ACTION_EVENT.SET_ALL_QUESTION_SCORE,
      payload: scores,
    });
    dispatch({ type: ACTION_EVENT.ON_UPDATE_QUESTIONS_SCROE, payload: data });
    dispatchCommunity({
      type: ACTION_EVENT.ON_UPDATE_ALL_COMMUNITY_DATA,
      payload: data,
    });
  }

  function updateReadinessScore(data) {
    dispatch({ type: ACTION_EVENT.ON_UPDATE_QUESTIONS_SCROE, payload: data })
    //dispatchCommunity({ type: ACTION_EVENT.ON_UPDATE_COMMUNITY_DATA, payload: data })
  }

  function setCommunityData(data) {
    dispatchCommunity({ type: ACTION_EVENT.SET_COMMUNITY_DATA, payload: data });
  }

  function setAllQuestionData(data) {
    dispatchQuestionScore({
      type: ACTION_EVENT.SET_ALL_QUESTION_SCORE,
      payload: data,
    });
  }

  const getBundleData = async () => {
    try {
      const bundleAppName = await BUNDLE_APP_NAME;
      
      // Validate bundleAppName
      if (!bundleAppName || typeof bundleAppName !== 'string') {
        logger.error("Invalid bundleAppName:", bundleAppName);
        return;
      }

      // Validate current user
      const currentUser = auth().currentUser;
      if (!currentUser || !currentUser.uid) {
        logger.error("No authenticated user found");
        return;
      }

      const result = await firestore()
        .collection("apps")
        .doc(bundleAppName)
        .collection("users")
        .doc(currentUser.uid)
        .get();

      if (result.exists) {
        dispatchBundle({
          type: ACTION_EVENT.SET_BUNDLE_DATA,
          payload: result.data()
        });
      }
    } catch (error) {
      logger.error("Error getting bundle data:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        profile,
        bundleData,
        community,
        questionsList,
        isSubscribe,
        showReactivation,
        isVisibleCommunity,
        questionScrore,
        mySubscription,
        setProfile,
        setBundleProfile,
        setNewProfile,
        setNewBundleProfile,
        setNotification,
        setProfileDetails,
        setProfilePicture,
        setBundleProfilePicture,
        setTestOptions,
        setSubscription,
        setTestHistory,
        deleteTestHistory,
        addFlashcardToBookmark,
        removeFlashcardFromBookmark,
        addFlashcardProgress,
        addQuestionToBookmark,
        removeQuestionFromBookmark,
        addQuestionProgress,
        addQuestionScore,
        setCommunityData,
        setQuestionsList,
        addAllQuestionScore,
        getSubscriptionDetails,
        setVisibleCommunity,
        loggingEnabled,
        setLoggingEnabled,
        setFontsOptions,
        setAllQuestionData,
        updateReadinessScore,
        getBundleData
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
